import React, { useState, useRef } from 'react';
import { Camera, Upload, X, User } from 'lucide-react';
import toast from 'react-hot-toast';

const ProfilePictureUpload = ({
  currentImage,
  onImageChange,
  size = 'large', // 'small', 'medium', 'large'
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const handleImageUpload = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      // Simulate upload - replace with actual Cloudflare upload
      const uploadResult = await simulateImageUpload(file);
      
      // Call onChange with the upload result
      onImageChange({
        file,
        previewUrl,
        uploadUrl: uploadResult.url,
        publicId: uploadResult.publicId,
        uploadedAt: new Date().toISOString()
      });

      toast.success('Profile picture updated successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Simulate upload - replace with actual Cloudflare upload
  const simulateImageUpload = (file) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          url: URL.createObjectURL(file), // Temporary URL for preview
          publicId: `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      }, 1500);
    });
  };

  const handleRemove = () => {
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`${sizeClasses[size]} rounded-full overflow-hidden border-4 transition-all duration-200 ${
          dragActive
            ? 'border-primary-500 shadow-lg'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Image Display */}
        <div className="w-full h-full relative group cursor-pointer" onClick={triggerFileInput}>
          {currentImage ? (
            <img
              src={currentImage}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <User className="w-8 h-8 text-gray-400" />
            </div>
          )}

          {/* Upload Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
            {uploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            ) : (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Camera className="w-6 h-6 text-white" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="absolute -bottom-2 -right-2 flex space-x-1">
        <button
          type="button"
          onClick={triggerFileInput}
          disabled={uploading}
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200 disabled:opacity-50"
          title="Upload new photo"
        >
          <Camera className="w-4 h-4" />
        </button>
        
        {currentImage && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={uploading}
            className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200 disabled:opacity-50"
            title="Remove photo"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Upload Instructions */}
      {dragActive && (
        <div className="absolute inset-0 bg-primary-100 bg-opacity-90 rounded-full flex items-center justify-center">
          <div className="text-center">
            <Upload className="w-6 h-6 text-primary-600 mx-auto mb-1" />
            <p className="text-xs text-primary-700 font-medium">Drop image here</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureUpload;
